/**
 * The products.js default-scene cascade, shared by the client and the server.
 *
 * `src/data/products.js` stores a legacy default-scene hash per
 * (channel, type, product) — as the products-map value itself
 * (`social.general.products.JB = "c367865d"`), as `{sceneId}`, or under a
 * `sets`/`defaults` sub-cascade keyed by set → layout/background. It predates
 * the `default_scenes` override table and is still the only source of a default
 * scene for the ~60 keys no override row covers.
 *
 * This lived inside `useInitialScene`, which then fetched the hash over
 * `POST /scene/load/v2`. Issue #567 requires the page to make no v2 read, so the
 * server resolves the same hash and returns the scene through
 * `/api/v3/default-scenes/resolved`. Both sides call THIS function: a
 * reimplementation on the server would be free to drift, and a drift shows up as
 * the wrong default scene on a channel nobody is watching.
 */

/** A products-map value: a bare hash, or a config carrying `sceneId`/`sets`. */
export type SceneConfig =
  | string
  | {
      sceneId?: string;
      sets?: Record<string, SetEntry>;
      [key: string]: unknown;
    };

/** A set entry: a bare hash, or per-layout / per-background hashes. */
export type SetEntry =
  | string
  | {
      default?: string;
      layouts?: Record<string, string>;
      layout?: Record<string, string>;
      backgrounds?: Record<string, string>;
      background?: Record<string, string>;
      [key: string]: unknown;
    };

export interface LegacySceneIdInput {
  /** `typeObj.products` — the (channel, type) products map. */
  products: Record<string, SceneConfig> | null | undefined;
  /** `typeObj.defaults` — the per-product / `"*"` set cascade. */
  defaults?: Record<string, Record<string, SetEntry>> | null;
  /** Product code, e.g. "JB". */
  product: string;
  /** Active set (`?set=`), e.g. "whats-new". Empty when none. */
  set?: string | null;
  /** Active layout (`?layout=`). */
  layout?: string | number | null;
  /** Active background (`?background=`). */
  background?: string | number | null;
}

/** `products[product]`, falling back to the map's first entry (legacy behavior). */
export function getSceneConfig(
  products: Record<string, SceneConfig> | null | undefined,
  product: string
): SceneConfig | null {
  if (!products) return null;
  const firstKey = Object.keys(products)[0];
  return products[product] ?? products[firstKey] ?? null;
}

/** Set entry from a config that carries `sets`. */
function getSetEntry(cfg: SceneConfig | SetEntry | null, set: string): SetEntry | null {
  if (!cfg || typeof cfg !== "object" || !set) return null;
  const sets = (cfg as {sets?: Record<string, SetEntry>}).sets;
  return sets?.[set] ?? null;
}

/**
 * Per-layout / per-background hashes from a set entry. Canonical key is plural
 * ("layouts", "backgrounds"); the singular form is accepted as a fallback.
 */
function getField(
  entry: SetEntry | null,
  field: "layout" | "background"
): Record<string, string> | null {
  if (!entry || typeof entry === "string") return null;
  const plural = (entry as Record<string, unknown>)[`${field}s`];
  const singular = (entry as Record<string, unknown>)[field];
  return (plural ?? singular ?? null) as Record<string, string> | null;
}

/**
 * Resolve the legacy default-scene hash for a key, most specific level first:
 * layout → background → set default → product-level hash. Null when the
 * registry defines none.
 */
export function resolveLegacySceneId({
  products,
  defaults,
  product,
  set,
  layout,
  background,
}: LegacySceneIdInput): string | null {
  if (!product) return null;

  const sceneConfig = getSceneConfig(products, product);
  const activeSet = set || "";

  // Priority order: the product's own set entry, then the type-level defaults
  // for this product, then the type-level wildcard.
  const sources: (SetEntry | null)[] = [
    getSetEntry(sceneConfig, activeSet),
    (activeSet && defaults?.[product]?.[activeSet]) || null,
    (activeSet && defaults?.["*"]?.[activeSet]) || null,
  ];

  if (layout) {
    for (const src of sources) {
      const layouts = getField(src, "layout");
      if (layouts?.[layout]) return layouts[layout];
    }
  }

  if (background) {
    for (const src of sources) {
      const bgs = getField(src, "background");
      if (bgs?.[background]) return bgs[background];
    }
  }

  for (const src of sources) {
    if (typeof src === "string" && src) return src;
    if (src && typeof src === "object" && src.default) return src.default;
  }

  // With a set active, do NOT fall back to the product-level hash: sets with no
  // scene of their own use "" in products.js, and the generic scene would apply
  // the wrong bg transforms for the set-specific context.
  if (activeSet) return null;

  if (!sceneConfig) return null;
  return typeof sceneConfig === "string"
    ? sceneConfig || null
    : (sceneConfig.sceneId ?? null) || null;
}
